import { onUnmounted, ref, type Ref } from "vue";
import { applyProgress, type RunProgressUpdate } from "@/composables/useRunProgress";
import type { RunHistory, ServiceHistory, ServiceRunStatus } from "@/types";

/**
 * Dev-only. Drives the run-history animations without a backend by feeding fabricated frames through the
 * same `applyProgress` path the live SignalR feed uses. Walks the selected run's own service graph
 * parent→child, flips each service to running then to a terminal status, and seeds one Failed and one
 * Warning so every edge colour (blue running, green succeeded, amber warning, red failed) shows in one pass.
 *
 * <p>It mutates the selected-run ref in place. There's no restore: selecting another run, or reloading,
 * replaces the ref with real data — a dev tool doesn't need an undo. Nothing here ships — the only
 * caller is behind `import.meta.env.DEV`.</p>
 */
export function useRunSimulator(selectedRun: Ref<RunHistory | null>) {
  const running = ref(false);
  let timer: ReturnType<typeof setTimeout> | null = null;

  const STEP_MS = 900; // running → terminal dwell per service; slow enough to watch the flowing dot.

  /**
   * Order services so a parent always lands before its children — the order the engine executes and the
   * order the edges should light up. Kahn's algorithm over `parentServiceIds`; any service left after a
   * cycle (shouldn't happen) is appended so nothing is dropped.
   */
  function topologicalOrder(services: ServiceHistory[]): ServiceHistory[] {
    const present = new Set(services.map((s) => s.serviceId));
    const byId = new Map(services.map((s) => [s.serviceId, s]));

    // Standard Kahn's: an in-degree per node (parents present in this graph) and a parent→children
    // adjacency, so relaxing a node touches only its children rather than rescanning every service.
    const inDegree = new Map<string, number>();
    const children = new Map<string, string[]>();
    for (const service of services) {
      const parents = service.parentServiceIds.filter((id) => present.has(id));
      inDegree.set(service.serviceId, parents.length);
      for (const parentId of parents) {
        const list = children.get(parentId) ?? [];
        list.push(service.serviceId);
        children.set(parentId, list);
      }
    }

    const ordered: ServiceHistory[] = [];
    const ready = services.filter((s) => inDegree.get(s.serviceId) === 0).map((s) => s.serviceId);
    while (ready.length > 0) {
      const nextId = ready.shift()!;
      ordered.push(byId.get(nextId)!);
      for (const childId of children.get(nextId) ?? []) {
        const remaining = inDegree.get(childId)! - 1;
        inDegree.set(childId, remaining);
        if (remaining === 0) ready.push(childId);
      }
    }

    // Any node never reaching in-degree 0 is in a cycle (shouldn't happen for a real run graph) —
    // append it so nothing is dropped from the walk.
    if (ordered.length < services.length) {
      const seen = new Set(ordered.map((s) => s.serviceId));
      for (const service of services) {
        if (!seen.has(service.serviceId)) ordered.push(service);
      }
    }
    return ordered;
  }

  /**
   * Pick which services end Failed and Warning, so the run shows all four colours. First leaf that isn't
   * the very first service becomes Warning; a different mid-graph service becomes Failed. Falls back to
   * whatever's available on tiny graphs — the point is variety, not a specific topology.
   */
  function pickTerminalOverrides(ordered: ServiceHistory[]): Map<string, ServiceRunStatus> {
    const overrides = new Map<string, ServiceRunStatus>();
    if (ordered.length === 0) return overrides;

    const childIds = new Set(ordered.flatMap((s) => s.parentServiceIds));
    const leaves = ordered.filter((s) => !childIds.has(s.serviceId));

    const warningTarget = leaves.find((s) => s !== ordered[0]) ?? leaves[0] ?? ordered[ordered.length - 1];
    if (warningTarget) overrides.set(warningTarget.serviceId, "Warning");

    const failTarget =
      ordered.find((s) => s.serviceId !== warningTarget?.serviceId && s !== ordered[0]) ??
      ordered.find((s) => s.serviceId !== warningTarget?.serviceId);
    if (failTarget) overrides.set(failTarget.serviceId, "Failed");

    return overrides;
  }

  /**
   * Build a full run-progress frame at a moment in the walk: services up to `runningIndex` are terminal,
   * the one at `runningIndex` is running (sent as "Running" — the canvas draws it as the blue animated
   * edge), and the rest are Pending. The run's own status is Running until the last service settles.
   */
  function frameAt(
    ordered: ServiceHistory[],
    runningIndex: number,
    overrides: Map<string, ServiceRunStatus>,
    runId: string,
    workflowId: string
  ): RunProgressUpdate {
    const services = ordered.map((service, index) => {
      let status: string;
      if (index < runningIndex) {
        status = overrides.get(service.serviceId) ?? "Succeeded";
      } else if (index === runningIndex) {
        status = "Running";
      } else {
        status = "Pending";
      }

      const settled = index < runningIndex;
      const terminal = overrides.get(service.serviceId);
      // Use the service's real total; only synthesise a count when the run carried none (a placeholder
      // with total 0 stays 0, rather than being inflated). Keep the Warning split non-negative.
      const total = service.total > 0 ? service.total : 10;
      const warnCount = terminal === "Warning" ? Math.min(1, total) : 0;
      return {
        serviceId: service.serviceId,
        serviceName: service.serviceName,
        status,
        started: index <= runningIndex,
        total: settled ? total : index === runningIndex ? total : 0,
        succeeded: settled && terminal !== "Failed" ? total - warnCount : 0,
        warning: settled ? warnCount : 0,
        failed: settled && terminal === "Failed" ? total : 0,
        neverResponded: 0,
        processingMs: settled ? 1200 : 0,
      };
    });

    const finished = runningIndex >= ordered.length;
    return {
      runId,
      workflowId,
      status: finished ? overallStatus(overrides) : "Running",
      services,
      errors: [],
    };
  }

  // A run is Failed if anything failed, else Warning if anything warned, else Succeeded — same
  // precedence the engine rolls up with.
  function overallStatus(overrides: Map<string, ServiceRunStatus>): string {
    const values = [...overrides.values()];
    if (values.includes("Failed")) return "Failed";
    if (values.includes("Warning")) return "Warning";
    return "Succeeded";
  }

  function push(frame: RunProgressUpdate) {
    const applied = applyProgress(selectedRun.value, frame);
    if (applied) selectedRun.value = applied;
  }

  function start() {
    const run = selectedRun.value;
    if (!run || run.services.length === 0 || running.value) return;

    running.value = true;

    const ordered = topologicalOrder(run.services);
    const overrides = pickTerminalOverrides(ordered);

    let index = 0;
    const tick = () => {
      if (!running.value) return;
      push(frameAt(ordered, index, overrides, run.id, run.workflowId));
      index += 1;
      if (index > ordered.length) {
        // Final frame (all terminal) is already pushed; hold it and stop the clock.
        running.value = false;
        timer = null;
        return;
      }
      timer = setTimeout(tick, STEP_MS);
    };
    tick();
  }

  // Stop the timer chain if the view unmounts mid-run, so a stray tick doesn't fire against a stale ref.
  onUnmounted(() => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    running.value = false;
  });

  return { start, running };
}
